import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import Password from "../Login/Password";
import {
  IcMenu, IcChevL, IcChevR, IcChevD, IcChevU,
  IcUserCircle, IcLock, IcSignOut,
} from "../../Icons";

// L89 (2026-06) — Clasifica la ruta actual al módulo del header.
// Toda la navegación es full-reload (href), así que window.location.pathname
// siempre está fresco. Reglas ordenadas: la primera que matchea gana, por eso
// las más específicas (CXP, Admin) van antes que las genéricas (Compras).
const REGLAS_MODULO = [
  // (2026-10) CRM: sus rutas son /MenuCrm y /Crm...
  ["crm", ["menucrm", "crm"]],
  ["logs", ["logs"]],
  ["usuarios", ["usuarios", "registrarempresas", "periodosadmin"]],
  ["nominas", ["nominas", "empleados", "faltascolaboradores", "retiros", "porretirar", "socios", "planteles"]],
  ["gastos", ["gastos", "pagosgastos", "presupuestos", "conceptosgastos"]],
  ["cxp", ["notascxp", "notascargo", "notascredito", "abonosproveedores", "abonosmaquileros", "anticiposproveedores", "bonificacionesproveedores", "devolucionesproveedoresadmin", "devolucionesproveedoreshab", "estadocuentacxp", "estadocuentamaq", "faltantesproveedores", "faltanteproveedores", "complementospago", "antiguedadsaldosvencidoproveedores", "comprascxp", "menucxp", "pagoscxp"]],
  ["admin", ["estadocuenta", "abonosfacturas", "abonos", "facturas", "bonificaciones", "antiguedadsaldos", "devolucionesadmin", "devolucionesfiscal", "ventasalderos", "menuadmin", "menufiscal"]],
  ["compras", ["ordenescompra", "comprascontenedor", "comprasdirectas", "comprasfaltante", "devolucionesproveedores", "devolucionestelas", "documentacion", "compraventa", "reportecomprasmaterial", "menucompras", "listadocompras", "comprascreate"]],
  ["muestras", ["muestras"]],
  ["produccion", ["ordenescorte", "cortespendientes", "cortediario", "cortesrollos", "articuloscortes", "reportecostoscortes", "reporteoperativo", "salidahabilitacion", "explosion", "procesos", "maquila", "cortes", "corte", "menuproduccion"]],
  ["inventarios", ["inventarios", "traspasos", "disponible", "pendrecibir", "apartados", "ajuste", "seguimientotelas", "menuinventarios", "menuventastelas"]],
  ["ventas", ["pedidos", "pedido", "cotizaciones", "ventadirecta", "pendientesurtir", "devoluciones", "seguimientopedidos", "menuventas"]],
  ["catalogos", ["articulos", "clientes", "proveedores", "colores", "tallas", "marcas", "familias", "lineas", "departamentos", "colecciones", "zonas", "empresas", "habilitacion", "catalogoprocesos", "clases", "subclases", "dibujos", "areas", "areasclientes", "compradores", "sociedades", "ciclos", "puestos", "variantestallas", "bancos", "telas", "tipostelas", "menucatalogos", "menuclientes", "menuconfiguraciones"]],
];

// (2026-10) Pantallas del menú de Producción cuyo nombre cae en otra regla
// ("ReporteOrdenesCompraPT" contiene "ordenescompra" → Compras). Van por nombre
// exacto para no arrastrar a las de Compras Directas del módulo de Compras.
const RUTAS_PRODUCCION = [
  "reporteordenescomprapt",
  "listadocomprasdirectas",
  "comprasdirectascreate",
  "seguimientoproduccion",
];

const clasificaModulo = (pathname) => {
  const p = String(pathname || "").toLowerCase().replace(/^\//, "");
  if (!p || p === "inicio") return "";
  if (RUTAS_PRODUCCION.includes(p.replace(/\/.*$/, ""))) return "produccion";
  for (const [mod, keys] of REGLAS_MODULO) {
    if (keys.some((k) => p.includes(k))) return mod;
  }
  return "";
};

// ── Navegación agrupada por sección ──────────────────────────────────────────
// Mismas rutas, mismo orden y mismos permisos que el menú de arriba de antes.
// `mod` es el módulo de REGLAS_MODULO que lo sombrea.
const NAV_SECTIONS = [
  {
    section: "Comercial",
    items: [
      { label: "CRM",       icon: "fas fa-handshake",       href: "/MenuCrm",       mod: "crm",       ver: (u) => u.menu_crm },
      { label: "Catálogos", icon: "fas fa-book-open",       href: "/MenuCatalogos", mod: "catalogos", ver: (u) => u.menu_articulos },
      { label: "Ventas",   icon: "fas fa-shopping-basket", href: "/MenuVentas",    mod: "ventas",    ver: (u) => u.menu_ventas },
    ],
  },
  {
    section: "Operación",
    items: [
      { label: "Compras",     icon: "fas fa-cart-plus", href: "/MenuCompras",     mod: "compras",     ver: (u) => u.menu_compras },
      { label: "Muestras",    icon: "fas fa-tshirt",    href: "/Muestras",        mod: "muestras",    ver: (u) => u.menu_muestras },
      { label: "Producción",  icon: "fas fa-cut",       href: "/MenuProduccion",  mod: "produccion",  ver: (u) => u.menu_produccion },
      { label: "Inventarios", icon: "fas fa-warehouse", href: "/MenuInventarios", mod: "inventarios", ver: (u) => u.menu_inventarios },
    ],
  },
  {
    section: "Finanzas",
    items: [
      { label: "Admin / CxC", icon: "fas fa-file-invoice-dollar", href: "/MenuAdmin",  mod: "admin",  ver: (u) => u.menu_admin },
      // (2026-10) también para quien sólo tiene Pagos y Saldos
      { label: "CxP",         icon: "fas fa-hand-holding-usd",    href: "/MenuCXP",    mod: "cxp",    ver: (u) => u.menu_adminCxP || u.menu_cxpPagos },
      { label: "Gastos",      icon: "fas fa-receipt",             href: "/MenuGastos", mod: "gastos", ver: (u) => u.menu_gastos },
    ],
  },
  {
    section: "Administración",
    items: [
      { label: "Colaboradores", icon: "fas fa-address-card", href: "/MenuNominas",  mod: "nominas",  ver: (u) => u.menu_nominas },
      { label: "Usuarios",      icon: "fas fa-users-cog",    href: "/MenuUsuarios", mod: "usuarios", ver: (u) => u.menu_usuarios },
      { label: "Logs",          icon: "fas fa-clipboard-list", href: "/MenuLogs",   mod: "logs",     ver: (u) => u.menu_logs },
    ],
  },
];

// Usuarios tipo Cliente: sólo su pantalla
const NAV_CLIENTE = [
  { section: "", items: [{ label: "Cliente", icon: "fas fa-user-tie", href: "/Clientes", ver: () => true }] },
];

// Cobradores y estados de cuenta
const NAV_COBRADORES = [
  {
    section: "",
    items: [
      { label: "Abonos Facturas",   icon: "fas fa-money-bill-wave",     href: "/AbonosNotasCreate",   ver: (u) => u.menu_cobradores },
      { label: "Estado de Cuenta",  icon: "fas fa-file-invoice-dollar", href: "/EdoCtaVistaFamilias", ver: (u) => u.menu_edoCta_Fam },
      { label: "Info Alumnos",      icon: "fas fa-id-card",             href: "/InfoHijos",           ver: (u) => u.menu_edoCta_Fam },
      { label: "Estado de Cuenta",  icon: "fas fa-file-invoice-dollar", href: "/EdoCtaVistaClientes", ver: (u) => u.menu_edoCta_Cliente },
    ],
  },
];

const Header = () => {
  const { isAuth, user } = useContext(AuthContext);

  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [modalPassword, setModalPassword] = useState(false);
  const [userMenuOpen,  setUserMenuOpen]  = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true"
  );

  // L89 (2026-06) — módulo activo según la ruta
  const currentPath = window.location.pathname;
  const moduloActivo = clasificaModulo(currentPath);

  // ── Body classes ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuth) return;
    document.body.classList.add("has-sidebar");
    if (collapsed) document.body.classList.add("sidebar-collapsed");
    return () => {
      document.body.classList.remove("has-sidebar");
      document.body.classList.remove("sidebar-collapsed");
    };
  }, [isAuth]); // eslint-disable-line

  if (!isAuth || !user) return null;

  const esCliente = user.tipo === "Cliente";
  const secciones = esCliente
    ? NAV_CLIENTE
    : user.menu_edoCta_Cliente || user.menu_cobradores
    ? NAV_COBRADORES
    : NAV_SECTIONS;

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar-collapsed", next);
    if (next) {
      document.body.classList.add("sidebar-collapsed");
      setUserMenuOpen(false);
    } else {
      document.body.classList.remove("sidebar-collapsed");
    }
  };

  return (
    <>
      {/* ── Mobile top bar ──────────────────────────────────────────────── */}
      <div className="sidebar-topbar d-lg-none">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          <IcMenu />
        </button>
        <a href="/Inicio">
          <img src={process.env.PUBLIC_URL + "/logoInstitucion.png"} height="34" alt="logo" />
        </a>
      </div>

      {/* ── Mobile overlay ──────────────────────────────────────────────── */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside
        className={[
          "app-sidebar",
          sidebarOpen ? "open"      : "",
          collapsed   ? "collapsed" : "",
        ].filter(Boolean).join(" ")}
      >
        {/* Brand */}
        <div className="sidebar-brand">
          <a href="/Inicio" className="sidebar-logo-link">
            <img
              src={process.env.PUBLIC_URL + "/logoInstitucion.png"}
              alt="logo"
              style={{ maxHeight: 44, width: "auto" }}
            />
          </a>
          <button
            className="sidebar-collapse-btn"
            onClick={toggleCollapse}
            title={collapsed ? "Expandir" : "Comprimir"}
          >
            {collapsed ? <IcChevR /> : <IcChevL />}
          </button>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          {secciones.map((sec) => {
            const visibleItems = sec.items.filter((i) => i.ver(user));
            if (visibleItems.length === 0) return null;

            return (
              <div key={sec.section} className="sidebar-section-group">
                {sec.section ? <div className="sidebar-section">{sec.section}</div> : null}

                {visibleItems.map((item) => {
                  const isActive = item.mod ? moduloActivo === item.mod : currentPath === item.href;
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className={`sidebar-item${isActive ? " active" : ""}`}
                      title={collapsed ? item.label : ""}
                    >
                      <i className={item.icon} />
                      <span className="sidebar-label">{item.label}</span>
                    </a>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="sidebar-footer">
          <div
            className="sidebar-item"
            onClick={() => !collapsed && setUserMenuOpen(!userMenuOpen)}
            title={collapsed ? (user.nombre || user.email || "Usuario") : ""}
            style={{ justifyContent: collapsed ? "center" : "space-between" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <IcUserCircle />
              <span className="sidebar-label" style={{ fontSize: "0.78rem" }}>
                {[user.nombre, user.apellido].filter(Boolean).join(" ") || user.email || "Usuario"}
              </span>
            </div>
            {!collapsed && (
              userMenuOpen ? <IcChevD /> : <IcChevU />
            )}
          </div>

          {userMenuOpen && !collapsed && (
            <div className="sidebar-submenu">
              {/* Antes los menús de Cliente y cobradores sólo traían Salir */}
              {secciones === NAV_SECTIONS ? (
                <div
                  className="sidebar-item sidebar-item-sm"
                  onClick={() => { setModalPassword(true); setUserMenuOpen(false); }}
                >
                  <IcLock />
                  <span>Cambiar contraseña</span>
                </div>
              ) : null}
              <a href="/logout" className="sidebar-item sidebar-item-sm" style={{ color: "#f87171" }}>
                <IcSignOut />
                <span>Cerrar sesión</span>
              </a>
            </div>
          )}
        </div>
      </aside>

      {/* ── Password modal ───────────────────────────────────────────────── */}
      <Modal size="sm" isOpen={modalPassword} toggle={() => setModalPassword(false)}>
        <ModalHeader toggle={() => setModalPassword(false)}>
          Cambio de contraseña
        </ModalHeader>
        <ModalBody>
          <Password />
        </ModalBody>
      </Modal>
    </>
  );
};

export default Header;
